import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Select } from '@/components/Select';
import { MultiSelect } from '@/components/MultiSelect';
import { CountryMultiSelect } from '@/components/CountryMultiSelect';
import { TrackerKpis } from '@/components/tracker/TrackerKpis';
import { TrackerFunnel, TrackerTrend } from '@/components/tracker/TrackerTrend';
import { TrackerBreakdown, type DimKey } from '@/components/tracker/TrackerBreakdown';
import { TrackerMovers } from '@/components/tracker/TrackerMovers';
import { countryName, flagOf, shortDay } from '@/components/tracker/format';
import { backfillTrackerCube, getPlayListing, getTrackerDashboard, type BreakdownRow, type MetricKey } from '@/api/tracker-dashboard';
import { useAuthStore } from '@/store/auth';
import { getApps } from '@/api/apps';
import { getCampaigns } from '@/api/tracker-campaigns';
import { getOffers } from '@/api/offers';
import { formatOfferLabel } from '@/lib/offers';

const DAY_MS = 24 * 60 * 60 * 1000;

function iso(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function daysAgo(n: number): string {
  return iso(new Date(Date.now() - n * DAY_MS));
}

type Preset = 'today' | 'yesterday' | '7d' | '30d' | '90d' | 'custom';

const PRESETS: { key: Preset; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
  { key: 'custom', label: 'Custom' },
];

function presetRange(p: Preset): { from: string; to: string } | null {
  switch (p) {
    case 'today':     return { from: daysAgo(0), to: daysAgo(0) };
    case 'yesterday': return { from: daysAgo(1), to: daysAgo(1) };
    case '7d':        return { from: daysAgo(6), to: daysAgo(0) };
    case '30d':       return { from: daysAgo(29), to: daysAgo(0) };
    case '90d':       return { from: daysAgo(89), to: daysAgo(0) };
    case 'custom':    return null;
  }
}

const SOURCE_TYPES = ['organic', 'organic_force', 'referral_force', 'referral_click', 'ad_click', 'playable'];

/**
 * Filter state lives in the URL, not in component state: a dashboard view is the
 * thing people paste into chat ("look at BR on this app"), and reloading after a
 * deploy should not silently reset what you were looking at.
 */
export function TrackerDashboard() {
  const [params, setParams] = useSearchParams();

  const preset = (params.get('preset') as Preset) || '7d';
  const fallback = presetRange(preset) ?? presetRange('7d')!;
  const from = params.get('from') || fallback.from;
  const to = params.get('to') || fallback.to;

  const appIds = (params.get('app') || '').split(',').filter(Boolean);
  const countries = (params.get('countries') || '').split(',').filter(Boolean);
  const campaignId = params.get('campaign') || '';
  const offerId = params.get('offer') || '';
  const partner = params.get('partner') || '';
  const sourceType = params.get('source') || '';
  const freelancer = params.get('freelancer') || '';
  const asa = params.get('asa') || '';
  const label = params.get('label') || '';
  const metric = (params.get('metric') as MetricKey) || 'deps';
  const tab = (params.get('tab') as DimKey) || 'app';
  const compare = params.get('cmp') !== '0';

  function patch(next: Record<string, string | null>) {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === '') p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: true });
  }

  function setPreset(k: Preset) {
    const r = presetRange(k);
    patch(r ? { preset: k, from: r.from, to: r.to } : { preset: k, from, to });
  }

  const query = {
    from, to,
    app_ids: appIds.length ? appIds : undefined,
    countries: countries.length ? countries : undefined,
    campaign_id: campaignId || undefined,
    offer_id: offerId || undefined,
    partner: partner || undefined,
    source_type: sourceType || undefined,
    freelancer: freelancer || undefined,
    asa: asa || undefined,
    label: label || undefined,
  };

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['tracker-dashboard', query],
    queryFn: () => getTrackerDashboard(query),
    // Today is recomputed live server-side, so a quiet refresh keeps the page
    // honest for whoever leaves it open on a second monitor.
    refetchInterval: to >= daysAgo(0) ? 120_000 : false,
    placeholderData: (prev) => prev,
  });

  // Play listing numbers ride on their own request: they come from a daily
  // import that lags by days, so they must never hold up the tracker view.
  const playQuery = { from, to, app_ids: query.app_ids, countries: query.countries };
  const { data: play } = useQuery({
    queryKey: ['tracker-dashboard-play', playQuery],
    queryFn: () => getPlayListing(playQuery),
    staleTime: 10 * 60_000,
    placeholderData: (prev) => prev,
  });

  // History older than the rollup reaches is missing, not zero. Say so rather
  // than drawing a flat line, and let a super admin fill it in place.
  const isSuperAdmin = useAuthStore((s) => s.admin?.role === 'super_admin');
  const queryClient = useQueryClient();
  const [backfillNote, setBackfillNote] = useState<string | null>(null);
  const backfill = useMutation({
    mutationFn: () => backfillTrackerCube(120),
    onSuccess: (r) => {
      setBackfillNote(`Built ${r.days} days (${r.from} → ${r.to}), ${r.rows} rows.`);
      queryClient.invalidateQueries({ queryKey: ['tracker-dashboard'] });
    },
    onError: (e: Error) => setBackfillNote(`Backfill failed: ${e.message}`),
  });
  const missingHistory = !!data && from < (data.cube_start ?? '9999-12-31') && from < daysAgo(0);

  const { data: apps = [] } = useQuery({ queryKey: ['apps'], queryFn: getApps });
  const { data: campaigns = [] } = useQuery({ queryKey: ['tracker-campaigns'], queryFn: getCampaigns });
  const { data: offers = [] } = useQuery({ queryKey: ['offers'], queryFn: getOffers });

  const appOptions = useMemo(() => [
    ...[...apps].sort((a, b) => a.name.localeCompare(b.name)).map((a) => ({
      value: a.id,
      label: a.bundle_id ? `${a.name} · ${a.bundle_id}` : a.name,
    })),
  ], [apps]);

  // Campaigns opted out of this page are invisible here, so neither they nor a
  // label used only by them may appear in a filter that would return nothing.
  const shown = useMemo(() => campaigns.filter((c) => !c.hide_from_dashboard), [campaigns]);

  const labelValues = useMemo(
    () => Array.from(new Set(shown.map((c) => c.label).filter((l): l is string => !!l))).sort(),
    [shown],
  );

  const campaignOptions = useMemo(() => [
    { value: '', label: 'All campaigns' },
    ...shown.filter((c) => !label || c.label === label).map((c) => ({ value: c.id, label: c.name })),
  ], [shown, label]);

  const partnerOptions = useMemo(() => [
    { value: '', label: 'All advertisers' },
    ...Array.from(new Set(offers.map((o) => o.partner))).filter(Boolean).sort()
      .map((p) => ({ value: p, label: p })),
  ], [offers]);

  const offerOptions = useMemo(() => [
    { value: '', label: 'All offers' },
    ...offers.filter((o) => !partner || o.partner === partner)
      .map((o) => ({ value: o.id, label: formatOfferLabel(o) })),
  ], [offers, partner]);

  const activeKeys: Partial<Record<DimKey, string>> = {
    app: appIds.length === 1 ? appIds[0] : undefined,
    country: countries.length === 1 ? countries[0] : undefined,
    campaign: campaignId || undefined,
    offer: offerId || undefined,
    partner: partner || undefined,
    app_country: appIds.length === 1 && countries.length === 1 ? `${appIds[0]} ${countries[0]}` : undefined,
    // The freelancer row carries an empty source half (see by_source in the API).
    source: freelancer === '1' ? ' 1' : (sourceType ? `${sourceType} 0` : undefined),
  };

  function drill(dim: DimKey, row: BreakdownRow) {
    const isActive = activeKeys[dim] === row.key;
    switch (dim) {
      case 'app': {
        const next = appIds.includes(row.key)
          ? appIds.filter((id) => id !== row.key)
          : [...appIds, row.key];
        return patch({ app: next.join(',') });
      }
      case 'country':  return patch({ countries: isActive ? null : row.key });
      case 'campaign': return patch({ campaign: isActive ? null : row.key });
      case 'offer':    return patch({ offer: isActive ? null : row.key });
      case 'partner':  return patch({ partner: isActive ? null : row.key });
      case 'app_country': {
        const [a, cc] = row.key.split(' ');
        return patch(isActive ? { app: null, countries: null } : { app: a ?? null, countries: cc ?? null });
      }
      case 'source': {
        const [s, fl] = row.key.split(' ');
        return patch(isActive
          ? { source: null, freelancer: null }
          : { source: s || null, freelancer: fl === '1' ? '1' : '0' });
      }
    }
  }

  type Chip = { k: string; text: string; clear: Record<string, string | null> };
  const chips = ([
    ...appIds.map((appId) => ({
      k: `app-${appId}`,
      text: (() => {
        const app = apps.find((a) => a.id === appId);
        return app ? `${app.name}${app.bundle_id ? ` · ${app.bundle_id}` : ''}` : appId;
      })(),
      clear: { app: appIds.filter((id) => id !== appId).join(',') || null },
    })),
    countries.length > 0 && {
      k: 'geo',
      text: countries.map((c) => `${flagOf(c)} ${countryName(c)}`).join(', '),
      clear: { countries: null },
    },
    campaignId && {
      k: 'campaign',
      text: campaigns.find((c) => c.id === campaignId)?.name ?? campaignId,
      clear: { campaign: null },
    },
    label && { k: 'label', text: `label: ${label}`, clear: { label: null } },
    partner && { k: 'partner', text: partner, clear: { partner: null } },
    offerId && {
      k: 'offer',
      text: (() => { const o = offers.find((x) => x.id === offerId); return o ? formatOfferLabel(o) : offerId; })(),
      clear: { offer: null },
    },
    sourceType && { k: 'source', text: sourceType, clear: { source: null } },
    freelancer && {
      k: 'freelancer',
      text: freelancer === '1' ? 'freelancer traffic' : 'own traffic',
      clear: { freelancer: null },
    },
    asa && {
      k: 'asa',
      text: asa === '1' ? 'Apple Search Ads' : 'not Apple Search Ads',
      clear: { asa: null },
    },
  ] as (Chip | '' | false)[]).filter((c): c is Chip => !!c);

  function clearAll() {
    patch({
      app: null, countries: null, campaign: null, offer: null,
      partner: null, source: null, freelancer: null, asa: null, label: null,
    });
  }

  if (error) {
    return (
      <div className="bg-danger-soft border border-danger/30 rounded-xl p-5 text-sm text-danger">
        Could not load tracker stats: {(error as Error).message}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border shadow-sm p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex bg-surface-muted rounded-lg p-0.5 gap-0.5">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPreset(p.key)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  preset === p.key ? 'bg-surface text-content shadow-sm' : 'text-content-subtle hover:text-content-muted'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date" value={from} max={to}
                onChange={(e) => patch({ from: e.target.value })}
                className="px-2.5 py-1.5 border border-border rounded-lg text-sm bg-surface"
              />
              <span className="text-content-subtle">—</span>
              <input
                type="date" value={to} min={from} max={daysAgo(0)}
                onChange={(e) => patch({ to: e.target.value })}
                className="px-2.5 py-1.5 border border-border rounded-lg text-sm bg-surface"
              />
            </div>
          )}

          <div className="ml-auto flex items-center gap-3 text-xs text-content-subtle">
            {data && (
              <span>
                {shortDay(data.range.from)} – {shortDay(data.range.to)} · vs {shortDay(data.prev_range.from)} – {shortDay(data.prev_range.to)}
              </span>
            )}
            {data?.live_today && (
              <span className="inline-flex items-center gap-1.5 text-success font-medium">
                <span className={`w-1.5 h-1.5 rounded-full bg-success ${isFetching ? 'animate-pulse' : ''}`} />
                live
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <MultiSelect
            value={appIds}
            options={appOptions}
            onChange={(v) => patch({ app: v.join(',') })}
            placeholder="All apps"
            searchable
            size="sm"
            className="min-w-[12rem] max-w-xs"
          />
          <div className="min-w-[14rem] max-w-sm flex-1">
            <CountryMultiSelect
              value={countries}
              onChange={(v) => patch({ countries: v.join(',') })}
              placeholder="All countries"
              size="sm"
            />
          </div>
          {labelValues.length > 0 && (
            <Select
              value={label}
              options={[{ value: '', label: 'All labels' }, ...labelValues.map((l) => ({ value: l, label: l }))]}
              onChange={(v) => patch({ label: v, campaign: null })}
              size="sm"
            />
          )}
          <Select value={campaignId} options={campaignOptions} onChange={(v) => patch({ campaign: v })} searchable size="sm" />
          <Select value={partner} options={partnerOptions} onChange={(v) => patch({ partner: v, offer: null })} searchable size="sm" />
          <Select value={offerId} options={offerOptions} onChange={(v) => patch({ offer: v })} searchable size="sm" />
          <Select
            value={sourceType}
            options={[{ value: '', label: 'All sources' }, ...SOURCE_TYPES.map((s) => ({ value: s, label: s }))]}
            onChange={(v) => patch({ source: v })}
            size="sm"
          />
          <Select
            value={freelancer}
            options={[
              { value: '', label: 'All traffic' },
              { value: '0', label: 'Own traffic' },
              { value: '1', label: 'Freelancers' },
            ]}
            onChange={(v) => patch({ freelancer: v })}
            size="sm"
          />
          <Select
            value={asa}
            options={[
              { value: '', label: 'ASA: all' },
              { value: '1', label: 'Apple Search Ads' },
              { value: '0', label: 'Not ASA' },
            ]}
            onChange={(v) => patch({ asa: v })}
            size="sm"
          />
        </div>

        {chips.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            {chips.map((c) => (
              <button
                key={c.k}
                onClick={() => patch(c.clear)}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium hover:bg-primary/20 transition-colors max-w-xs"
              >
                <span className="truncate">{c.text}</span>
                <span className="text-primary/70">✕</span>
              </button>
            ))}
            <button onClick={clearAll} className="px-2 py-1 text-xs font-medium text-danger hover:bg-danger-soft rounded-md transition-colors">
              Clear all
            </button>
          </div>
        )}
      </div>

      {missingHistory && (
        <div className="flex flex-wrap items-center gap-3 bg-warning-soft border border-warning/30 rounded-xl px-4 py-3 text-sm text-content">
          <span>
            Rolled-up history starts {data!.cube_start ? shortDay(data!.cube_start) : 'nowhere yet'} — days before
            that show as zero because they have not been aggregated, not because there was no traffic.
          </span>
          {isSuperAdmin && (
            <button
              onClick={() => backfill.mutate()}
              disabled={backfill.isPending}
              className="ml-auto px-3 py-1.5 text-xs font-semibold rounded-lg bg-surface border border-border hover:bg-surface-muted disabled:opacity-60 transition-colors"
            >
              {backfill.isPending ? 'Building…' : 'Build history'}
            </button>
          )}
          {backfillNote && <span className="w-full text-xs text-content-muted">{backfillNote}</span>}
        </div>
      )}

      {isLoading || !data ? (
        <DashboardSkeleton />
      ) : (
        <>
          <TrackerKpis
            totals={data.totals}
            prev={data.prev_totals}
            daily={data.daily}
            metric={metric}
            onMetric={(m) => patch({ metric: m })}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <TrackerTrend
                daily={data.daily}
                prevDaily={data.prev_daily}
                metric={metric}
                onMetric={(m) => patch({ metric: m })}
                compare={compare}
                onCompare={(v) => patch({ cmp: v ? null : '0' })}
              />
            </div>
            <TrackerFunnel totals={data.totals} />
          </div>

          <TrackerMovers
            rows={data.app_country}
            onDrill={(row) => drill('app_country', row)}
          />

          <TrackerBreakdown
            data={data}
            play={play}
            tab={tab}
            onTab={(t) => patch({ tab: t })}
            onDrill={drill}
            activeKeys={activeKeys}
          />
        </>
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="bg-surface rounded-xl border border-border p-4 h-32">
            <div className="h-3 bg-surface-muted rounded w-16 mb-3" />
            <div className="h-6 bg-surface-muted rounded w-20" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-surface rounded-xl border border-border h-[22rem]" />
        <div className="bg-surface rounded-xl border border-border h-[22rem]" />
      </div>
      <div className="bg-surface rounded-xl border border-border h-64" />
    </div>
  );
}
